import { getCoreUtils, nz } from "../../../Admin/kReusableCoreTS/kCoreHandler";
import { AppContact, AppIndex, AppRow } from "./AppIndex";
import { AppLiveAnswer, AppLiveCursor, AppLiveItem, AppLiveListItem, AppLiveRules } from "./AppLiveRules";
import { AppLiveKick, AppLiveLatch, IAppLiveClock, IAppLivePost } from "./AppLiveFeed";
import { AppSignUpApi } from "./AppSignUpApi";
import { AppTypingCards, IAppTypingCards } from "./AppLiveTyping";
import { AppStanding, AppStandingNow } from "./AppStanding";
import { AppWaLink, AppWaNow } from "./AppWaLink";

// AppLiveList - THE CHAT LIST KEEPS UP WITHIN SECONDS (owner, 2026-09-28: "Receiving messages should be almost instant -
// anything more than 10 seconds should be broken"). Before, only the 15-minute rebuild wrote `index.json`, so a message
// in any chat but the open one moved nothing - no card to the top, no "today", and a brand-new chat had no card at all.
//
// ⭐ ONE LONG POLL PER TAB to `/live` in its `list` mode (the service holds it until a 1:1 message lands, like the open
// chat's feed - `LiveRun`). The answer carries message IDS, times and chats, NEVER words (the card shows none), plus a
// title for any chat the index has not got yet and the index's `built` time. `AppLiveListRules.Apply` folds that into the
// index in hand: the card's time, "N new" (their messages since the page loaded, not in the chat being read), a card
// for a new chat (`IsNew` - it opens that chat's provisional page, `#n/<chat>`), the owed-reply task gone when the owner
// answered from the phone. A newer `built` means the rebuild has run: the host re-reads `index.json` quietly.
//
// ⭐ IT KEEPS ASKING WHILE THE TAB IS HIDDEN (unlike the open chat's feed, which pauses): one held request per ~25 s is
// nothing, and it is what lets the tab's title say "(2) WACommander" while the owner is in another tab. A browser may
// slow the timer between answers in a background tab; `Kick` (tab shown, network back) asks at once.
// ⛔ READ-ONLY (decision 9): nothing here reaches a sender. ⛔ Nothing is stored - the counts live for the visit.
// ⛔ DORMANT WITH `/live`: a service without the route answers the status document -> `Off`, never asked again.
// ⭐ TYPING (2026-09-30): each ask says which chats show "typing…" now (`typing: [ids]`); the service answers EARLY when
// that changes, and the answer's `typing` is painted onto the cards (`AppTypingCards`, no list redraw). A line nobody
// renewed goes after `AppLiveRules.TypingSafetyMs` (the service's own expiry is shorter, so normally it clears first).
// ⭐ ACCOUNT WARNINGS (2026-10-01, `AppStanding`): each ask also says which account standing the page shows
// (`standing: "<sig>"`); the answer's `standing` is handed to `OnStanding` (the banner under the top bar) ONLY when it
// differs from what is shown. It stays while the feed is merely reconnecting (a restriction does not end because a
// request failed) and goes when the feed stops for good (another archive, a sign-out, a service without `/live`).

/** What the list feed hands its host - the host owns the index and the screen. */
export interface IAppLiveListHost {
    OnList(Items: AppLiveListItem[], Names: Record<string, string>, Built: string): void;
}

/** Who is asking - read when the feed is made. */
export class AppLiveListWho {
    public ServiceUrl: string = "";
    public Phone: string = "";
    public Key: string = "";
}

export class AppLiveList {
    /** After an answer, the next ask goes after this pause. */
    public static readonly AgainMs: number = 300;
    /** Asked from this long before the index was built - a message the rebuild just missed is still counted. */
    public static readonly FloorSlackMs: number = 600000;

    private Host: IAppLiveListHost;
    private Who: AppLiveListWho;
    private Transport: IAppLivePost;
    private Cursor: AppLiveCursor = new AppLiveCursor();
    private Begun: boolean = false;
    private Stopped: boolean = false;
    private InFlight: boolean = false;
    private Latch: AppLiveLatch;
    private Failures: number = 0;
    private AnsweredMs: number = 0;
    /** The service's last answer was a refusal that clears ("not running", "busy" - `retry`): no updates are coming now. */
    private Waiting: boolean = false;
    public Asked: number = 0;
    public Now: () => number = Date.now;
    /** ⭐ Where the typing chats are painted - the page's cards in a browser, nothing in the suite unless it sets one. */
    public Typing: IAppTypingCards = AppTypingCards.ForPage();
    /** The chats shown as typing now - sent with every ask. */
    private TypingShown: string[] = [];
    private TypingTimer: number = 0;
    /** ⭐ Who is told when the account's standing changes (`AppMain` -> the banner). A field, like `Typing`. */
    public OnStanding: (Now: AppStandingNow) => void = AppLiveList.NoStanding;
    /** The standing shown now - its signature goes with every ask. */
    private StandingShown: AppStandingNow = new AppStandingNow();
    /**
     * ⛔ VERSION SKEW (2026-10-01): what the ask ECHOES - the service's own signature as RECEIVED (`AppStanding.EchoOf`),
     * which is `SigOf(StandingShown)` unless the service sent an end time this build cannot read. Echoing what is DRAWN
     * then never matched the service's signature, and every ask was answered at once.
     */
    private StandingEcho: string = AppStanding.Ok;
    /**
     * ⭐ WHATSAPP LINK STATE (2026-10-01, `AppWaLink`): who is told when the service's word for the subscriber's own
     * WhatsApp link changes (`AppMain` -> the top bar's line, the re-link banner). Null = the service does not say.
     */
    public OnWa: (Now: AppWaNow | null) => void = AppLiveList.NoWa;
    /** The link state held now (null = not known), and the word the next ask echoes ("unknown" until the service says). */
    private WaShown: AppWaNow | null = null;
    private WaEcho: string = AppWaLink.Unknown;
    /** ⭐ Told after an answer that was NOT a list (a failed ask, "try again in N s") - the top bar's line may have moved. */
    public OnHealth: () => void = AppLiveList.NoHealth;
    /** When the asks began to FAIL in a row (0 = not failing) - the top bar says "not reachable" only after `FeedDampMs`. */
    private FailingSinceMs: number = 0;

    public constructor(Host: IAppLiveListHost, Who: AppLiveListWho, Transport: IAppLivePost, Clock: IAppLiveClock) {
        this.Host = Host;
        this.Who = Who;
        this.Transport = Transport;
        this.Latch = new AppLiveLatch(Clock, this.Ask);
    }

    /** Start from the index's built time (less `FloorSlackMs`); "" = from the newest the service has. Once. */
    public Start = (BuiltIso: string): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            if (this.Begun === true || this.Stopped === true) { return; }
            this.Begun = true;
            this.Cursor.Begin(AppLiveList.FloorOf(BuiltIso, this.Now()), []);
            this.Latch.Ask();
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppLiveList.Start");
        }
    };

    /** The floor for a built time: `FloorSlackMs` before it; no (or a future) built time = the last hour. */
    public static FloorOf = (BuiltIso: string, NowMs: number): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            let Ms: number = Date.parse(nz(BuiltIso));
            if (isNaN(Ms) === true || Ms > NowMs) { Ms = NowMs - 3000000; }
            return new Date(Ms - AppLiveList.FloorSlackMs).toISOString();
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppLiveList.FloorOf");
            return "";
        }
    };

    /** Another archive, a sign-out: no more asking. */
    public Stop = (): void => {
        // ERROR-SUPPRESSED-JUSTIFIED: no KErrorMode guard - a stop a latched gate skipped would leave a poll running.
        try {
            this.Stopped = true;
            const PollBusy: boolean = this.Latch.Timer !== 0;
            if (PollBusy === true) { this.Latch.Clock.Cancel(this.Latch.Timer); this.Latch.Timer = 0; }
            const Asking: boolean = this.InFlight === true;
            if (Asking === true) { this.InFlight = false; this.Transport.Abort(); }
            if (this.TypingTimer !== 0) { this.Latch.Clock.Cancel(this.TypingTimer); this.TypingTimer = 0; }
            if (this.TypingShown.length > 0) { this.TypingShown = []; this.Typing.Show([]); }
            this.ShowStanding(new AppStandingNow());
            this.ShowWa(null);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppLiveList.Stop");
        }
    };

    /** Nobody listens (the suite, a page without a banner). DOCUMENTED EXCEPTION: a no-op.
     *  ERROR-SUPPRESSED-JUSTIFIED: nothing to do, cannot throw. */
    private static NoStanding = (Now: AppStandingNow): void => {
        return;
    };

    /**
     * ⭐ The account's standing as the service says it now. ⛔ The host is told ONLY ON A CHANGE (a banner written again
     * with the same words is announced again by a screen reader); what is shown is remembered for the next ask.
     */
    public ShowStanding = (Now: AppStandingNow, Echo: string = ""): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            // ⛔ The echo is noted BEFORE the "same as shown" test: an end time this build cannot read changes nothing
            // on screen, and must still go back to the service that sent it.
            this.StandingEcho = AppStanding.SigOf(Now);
            if (Echo.length > 0) { this.StandingEcho = Echo; }
            if (AppStanding.SigOf(Now) === AppStanding.SigOf(this.StandingShown)) { return; }
            this.StandingShown = Now;
            this.OnStanding(Now);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppLiveList.ShowStanding");
        }
    };

    /** The standing shown now - for the suite and the host. DOCUMENTED EXCEPTION: a field read.
     *  ERROR-SUPPRESSED-JUSTIFIED: cannot throw. */
    public StandingNow = (): AppStandingNow => {
        return this.StandingShown;
    };

    /** Nobody listens. DOCUMENTED EXCEPTION: a no-op. ERROR-SUPPRESSED-JUSTIFIED: nothing to do, cannot throw. */
    private static NoWa = (Now: AppWaNow | null): void => {
        return;
    };

    /** Nobody listens. DOCUMENTED EXCEPTION: a no-op. ERROR-SUPPRESSED-JUSTIFIED: nothing to do, cannot throw. */
    private static NoHealth = (): void => {
        return;
    };

    /**
     * ⭐ The subscriber's own WhatsApp link state as the service says it now (null = it did not say). ⛔ The word RECEIVED
     * is what the next ask echoes - noted BEFORE the "same as held" test, so a word this build has no line for still goes
     * back (no loop). ⛔ The host is told ONLY ON A CHANGE (the top bar's live region must not be written again).
     */
    public ShowWa = (Now: AppWaNow | null): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            // ⚠ No word (an answer without the field): the page holds nothing again, and says so - the next service that
            // does say answers at once instead of holding an ask whose stale echo happens to match.
            this.WaEcho = AppWaLink.Unknown;
            if (Now !== null) { this.WaEcho = Now.State; }
            if (AppLiveList.WaSig(Now) === AppLiveList.WaSig(this.WaShown)) { return; }
            this.WaShown = Now;
            this.OnWa(Now);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppLiveList.ShowWa");
        }
    };

    /** One string for a held link state ("" = none). DOCUMENTED EXCEPTION: joins two fields.
     *  ERROR-SUPPRESSED-JUSTIFIED: cannot throw. */
    private static WaSig = (Now: AppWaNow | null): string => {
        if (Now === null) { return ""; }
        return Now.State + "@" + Now.Since;
    };

    /** The link state held now (null = the service does not say). DOCUMENTED EXCEPTION: a field read.
     *  ERROR-SUPPRESSED-JUSTIFIED: cannot throw. */
    public WaNow = (): AppWaNow | null => {
        return this.WaShown;
    };

    /**
     * How long the asks have been failing in a row (ms); 0 = they are not. ⛔ NEVER 0 WHILE FAILING: the top bar reads 0
     * as "no failed ask" (a feed that went silent), and a first failure read in its own millisecond then said "Service
     * not reachable" for a one-second blip (found in real Chrome, 2026-10-01).
     */
    public FailingMs = (): number => {
        if (getCoreUtils().KErrorMode === true) { return 0; }
        try {
            if (this.Failures === 0 || this.FailingSinceMs === 0) { return 0; }
            return Math.max(1, this.Now() - this.FailingSinceMs);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppLiveList.FailingMs");
            return 0;
        }
    };

    /** The service's last answer was "try again in N s" (not running yet, busy). DOCUMENTED EXCEPTION: a field read.
     *  ERROR-SUPPRESSED-JUSTIFIED: cannot throw. */
    public IsWaiting = (): boolean => {
        return this.Waiting === true;
    };

    /**
     * A held ask is answered within `HoldMs` even when nothing happened, so no answer for this long means the feed is not
     * live, whatever the last answer said (a request hanging on a dead connection, a laptop that slept).
     */
    public static readonly StaleMs: number = AppLiveRules.HoldMs + 35000;

    /**
     * ⭐ How the feed is doing, for the top bar's connection line (`AppTopBarRules.Status`): "connected" while answers keep
     * coming (the last one within `StaleMs`) and none failed since; "reconnecting" after a failed ask, while the service
     * says "try again in N s" (not running yet, busy), or when answers stopped coming; "off" when stopped or the service
     * has no `/live`; "unknown" before the first answer. ⛔ Never "connected" on an old answer.
     */
    public Health = (): string => {
        if (getCoreUtils().KErrorMode === true) { return "unknown"; }
        try {
            if (this.Stopped === true || AppLiveListRules.Off === true) { return "off"; }
            if (this.Failures > 0 || this.Waiting === true) { return "reconnecting"; }
            if (this.AnsweredMs > 0 && this.Now() - this.AnsweredMs > AppLiveList.StaleMs) { return "reconnecting"; }
            if (this.AnsweredMs > 0) { return "connected"; }
            return "unknown";
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppLiveList.Health");
            return "unknown";
        }
    };

    /** ⭐ Ask NOW (tab shown, network back): a pending wait is cut short. */
    public Kick = (): void => {
        AppLiveKick.Now(this.Stopped, false, this.InFlight, this.Begun, this.Latch, "AppLiveList.Kick");
    };

    private Ask = (): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            this.Latch.Timer = 0;
            if (this.Stopped === true || this.InFlight === true || AppLiveListRules.Off === true) { return; }
            const Url: string = AppSignUpApi.UrlFor(this.Who.ServiceUrl, AppLiveRules.Route);
            if (Url.length === 0) { this.Stop(); return; }
            let Gap: number = -1;
            if (this.AnsweredMs > 0) { Gap = Math.max(0, this.Now() - this.AnsweredMs); }
            this.InFlight = true;
            this.Asked = this.Asked + 1;
            this.Transport.Post(Url, AppLiveRules.BodyOf(this.Who.Phone, this.Who.Key, "", this.Cursor.Floor(),
                this.Cursor.Seen(), AppLiveRules.ListMode, "", [], Gap, this.TypingShown,
                this.StandingEcho, this.WaEcho), this.OnAnswer);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppLiveList.Ask");
        }
    };

    /** Hand what is new to the host and ask again; or wait; or stop - `AppLiveRules.Parse` decides which. */
    public OnAnswer = (Status: number, Text: string): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            if (this.InFlight === false || this.Stopped === true) { return; }
            this.InFlight = false;
            const Got: AppLiveAnswer = AppLiveRules.Parse(Status, Text);
            // ⛔ THE HOST IS TOLD WHEN THE FEED ENDS (2026-10-13): until the first answer the top bar says "Checking
            // WhatsApp…" (`AppTopBarRules.Status`), and an answer that turns the feed off is the end of that check - told
            // by nobody, the line kept saying "Checking…" until the next 15 s tick.
            if (Got.Off === true || (Got.Ok === true && Got.List === false)) { AppLiveListRules.Off = true; this.Stop(); this.OnHealth(); return; }
            if (Got.Stop === true) { this.Stop(); this.OnHealth(); return; }
            if (Got.Ok === true) {
                this.Failures = 0;
                this.Waiting = false;
                this.AnsweredMs = this.Now();
                const Fresh: AppLiveListItem[] = AppLiveListRules.Unseen(this.Cursor, Got.ListItems);
                this.Cursor.Took(AppLiveListRules.AsItems(Got.ListItems));
                this.Host.OnList(Fresh, Got.Names, Got.Built);
                if (Got.TypingKnown === true) { this.ShowTyping(Got.TypingChats); }
                if (Got.StandingKnown === true) { this.ShowStanding(Got.Standing, Got.StandingEcho); }
                // ⭐ A list answer WITHOUT `wa` = this service does not say (older, single-session): nothing is claimed.
                this.ShowWa(AppLiveList.WaOf(Got));
                this.Again(AppLiveList.AgainMs);
                return;
            }
            if (Got.RetrySec > 0) { this.OnTryAgain(Got); return; }
            this.OnNoAnswer();
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppLiveList.OnAnswer");
        }
    };

    /** The service answered "try again in N s" - it is reachable; it may have said how WhatsApp is doing. */
    private OnTryAgain = (Got: AppLiveAnswer): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            this.Waiting = true;
            this.Failures = 0;
            this.FailingSinceMs = 0;
            this.ShowWa(AppLiveList.WaOf(Got));
            // ⛔ THE NEXT ASK HOLDS NOTHING (verifier 2026-10-01): echoing the refusal's word let the service HOLD the
            // first list ask of a tenant that came up with the same word - up to `HoldMs` under "Waiting for the
            // service…". With `unknown` that ask is answered at once; a list answer's word is echoed as before.
            this.WaEcho = AppWaLink.Unknown;
            this.Again(Got.RetrySec * 1000);
            this.OnHealth();
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppLiveList.OnTryAgain");
        }
    };

    /** The ask failed (no answer, a 5xx): the failing clock runs from the FIRST failure, and the backoff grows. */
    private OnNoAnswer = (): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            // ⛔ NO ANSWER AT ALL: whatever the service said before ("try again"), it is not saying it now - without
            // this the line kept the refusal's word for as long as the service could not be reached.
            this.Waiting = false;
            // ⛔ AND THE NEXT ASK HOLDS NOTHING (found in real Chrome): the first ask that got through echoed the old
            // word, matched, and was HELD - "Service not reachable" stayed up to `HoldMs` after the service was back.
            // The held word itself stays (`WaShown`), for the damping rule.
            this.WaEcho = AppWaLink.Unknown;
            if (this.Failures === 0) { this.FailingSinceMs = this.Now(); }
            this.Failures = this.Failures + 1;
            this.Again(AppLiveRules.BackoffMs(this.Failures));
            this.OnHealth();
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppLiveList.OnNoAnswer");
        }
    };

    /** The answer's link state, or null when it carried none. DOCUMENTED EXCEPTION: a field read.
     *  ERROR-SUPPRESSED-JUSTIFIED: cannot throw. */
    private static WaOf = (Got: AppLiveAnswer): AppWaNow | null => {
        if (Got.WaKnown !== true) { return null; }
        return Got.Wa;
    };

    /** ⭐ These chats type now: painted, remembered for the next ask, and cleared if nobody renews them. */
    public ShowTyping = (Chats: string[]): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            const ShownNow: string[] = AppLiveRules.TypingChatsOf(Chats);
            this.TypingShown = ShownNow;
            this.Typing.Show(ShownNow);
            const Renew: number = this.TypingTimer;
            const Stale: () => void = this.OnTypingStale;
            if (Renew !== 0) { this.Latch.Clock.Cancel(Renew); this.TypingTimer = 0; }
            if (ShownNow.length > 0) { this.TypingTimer = this.Latch.Clock.Later(AppLiveRules.TypingSafetyMs, Stale); }
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppLiveList.ShowTyping");
        }
    };

    /** Nobody renewed the typing lines (the feed stopped answering): they go. */
    private OnTypingStale = (): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            this.TypingTimer = 0;
            this.ShowTyping([]);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppLiveList.OnTypingStale");
        }
    };

    /** The chats shown as typing now - for the suite. DOCUMENTED EXCEPTION: a copy of a field.
     *  ERROR-SUPPRESSED-JUSTIFIED: cannot throw. */
    public TypingNow = (): string[] => {
        return this.TypingShown.slice(0);
    };

    private Again = (Ms: number): void => {
        AppLiveKick.Again(this.Stopped, false, Ms, this.Latch, "AppLiveList.Again");
    };
}

/**
 * The list feed's rules over the index in hand - pure (no DOM, no transport), so the suite drives them directly.
 * ⭐ `Unread` is chat id -> their messages since the page loaded that the owner has not opened; it lives beside the
 * index (a re-read index is a new object) and `Carry` puts it back on the new one, with the new chats not in it yet.
 */
export class AppLiveListRules {
    /** ⛔ The service has no list mode (dormant / older): never asked again this visit. */
    public static Off: boolean = false;
    /** The `standing` of a chat the index has not got yet - its card says "new chat". */
    public static readonly NewStanding: string = "new";

    /** The items the cursor has not taken yet (an id is handed over once). */
    public static Unseen = (Cursor: AppLiveCursor, Items: AppLiveListItem[]): AppLiveListItem[] => {
        const Out: AppLiveListItem[] = [];
        if (getCoreUtils().KErrorMode === true) { return Out; }
        try {
            for (const One of Items) { if (Cursor.Has(One.Id) === false) { Out.push(One); } }
            return Out;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppLiveListRules.Unseen");
            return Out;
        }
    };

    /** List items as the cursor's items (id + time). */
    public static AsItems = (Items: AppLiveListItem[]): AppLiveItem[] => {
        const Out: AppLiveItem[] = [];
        if (getCoreUtils().KErrorMode === true) { return Out; }
        try {
            for (const One of Items) {
                const Item: AppLiveItem = new AppLiveItem();
                Item.Id = One.Id;
                Item.At = One.At;
                Out.push(Item);
            }
            return Out;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppLiveListRules.AsItems");
            return Out;
        }
    };

    /**
     * Fold new list messages into the index (oldest first). True when anything the list shows changed. `Open` = the chat
     * id being read now (its messages are not "new" - the owner sees them arrive).
     */
    public static Apply = (Index: AppIndex, Items: AppLiveListItem[], Names: Record<string, string>,
                           Unread: Record<string, number>, Open: string): boolean => {
        if (getCoreUtils().KErrorMode === true) { return false; }
        try {
            let Changed: boolean = false;
            for (const One of Items) {
                let Who: AppContact | null = AppLiveListRules.ContactOf(Index, One.Chat);
                if (Who === null) { Who = AppLiveListRules.Newcomer(Index, One, Names); }
                if (One.At <= Who.LastIso) { continue; }
                Who.LastIso = One.At;
                Who.DaysSinceLast = 0;
                Changed = true;
                if (One.FromMe === true) { AppLiveListRules.Answered(Index, Who, Unread); continue; }
                if (One.Chat === nz(Open)) { continue; }
                Unread[One.Chat] = (Unread[One.Chat] || 0) + 1;
            }
            AppLiveListRules.Carry(Index, Unread);
            return Changed;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppLiveListRules.Apply");
            return false;
        }
    };

    /** The index row for a chat id, or null. */
    public static ContactOf = (Index: AppIndex, Chat: string): AppContact | null => {
        if (getCoreUtils().KErrorMode === true) { return null; }
        try {
            for (const One of Index.Contacts) { if (One.ChatId === Chat) { return One; } }
            return null;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppLiveListRules.ContactOf");
            return null;
        }
    };

    /** A card for a chat the index has not got: its title (service's, else the number), `IsNew`, no page yet. */
    private static Newcomer = (Index: AppIndex, One: AppLiveListItem, Names: Record<string, string>): AppContact => {
        const Out: AppContact = new AppContact();
        if (getCoreUtils().KErrorMode === true) { return Out; }
        try {
            Out.ChatId = One.Chat;
            Out.Title = AppLiveListRules.TitleOf(One.Chat, Names);
            Out.IsNew = true;
            Out.Standing = AppLiveListRules.NewStanding;
            Out.LastIso = "";
            Index.Contacts.push(Out);
            return Out;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppLiveListRules.Newcomer");
            return Out;
        }
    };

    /** The service's title, else "+<digits>" for a phone chat, else "New chat" (a lid with no name). */
    public static TitleOf = (Chat: string, Names: Record<string, string>): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            const Named: string | undefined = Names[Chat];
            if (Named !== undefined && Named.trim().length > 0) { return Named.trim(); }
            if (/@s\.whatsapp\.net$/.test(Chat) === true) { return "+" + Chat.split("@")[0]; }
            return "New chat";
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppLiveListRules.TitleOf");
            return "";
        }
    };

    /**
     * The owner answered (from the phone, or this app): nothing is new any more, and a reply owed is not owed now -
     * the owed-reply task row (`r-`, source `reply`) leaves the lists until the rebuild says otherwise.
     */
    private static Answered = (Index: AppIndex, Who: AppContact, Unread: Record<string, number>): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            Unread[Who.ChatId] = 0;
            if (Who.Standing === "owed") { Who.Standing = "active"; }
            if (Who.Href.length === 0) { return; }
            const Kept: AppRow[] = [];
            for (const Row of Index.Tasks) {
                if (Row.Href === Who.Href && Row.Source === "reply" && (Row.State === "open" || Row.State === "")) {
                    Who.OpenTasks = Math.max(0, Who.OpenTasks - 1);
                    continue;
                }
                Kept.push(Row);
            }
            Index.Tasks = Kept;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppLiveListRules.Answered");
        }
    };

    /** The counts onto the index's rows (a re-read index is new objects). */
    public static Carry = (Index: AppIndex, Unread: Record<string, number>): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            for (const One of Index.Contacts) {
                let N: number = 0;
                if (typeof Unread[One.ChatId] === "number") { N = Unread[One.ChatId]; }
                One.Unread = N;
            }
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppLiveListRules.Carry");
        }
    };

    /**
     * A re-read index: the new chats it does not have yet are kept (their cards stay until the rebuild makes their page),
     * the ones it has now become ordinary cards; then the counts. `Old` = the index being replaced.
     */
    public static Adopt = (Fresh: AppIndex, Old: AppIndex, Unread: Record<string, number>): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            for (const One of Old.Contacts) {
                if (One.IsNew === false) { continue; }
                if (AppLiveListRules.ContactOf(Fresh, One.ChatId) === null) { Fresh.Contacts.push(One); }
            }
            AppLiveListRules.Carry(Fresh, Unread);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppLiveListRules.Adopt");
        }
    };

    /** The chat was opened: nothing in it is new any more. */
    public static Read = (Index: AppIndex, Unread: Record<string, number>, Chat: string): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            if (nz(Chat).length === 0) { return; }
            Unread[Chat] = 0;
            AppLiveListRules.Carry(Index, Unread);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppLiveListRules.Read");
        }
    };

    /** Every chat's "new" count, summed - the tab title's number. */
    public static Total = (Unread: Record<string, number>): number => {
        if (getCoreUtils().KErrorMode === true) { return 0; }
        try {
            let Sum: number = 0;
            for (const Chat of Object.keys(Unread)) { Sum = Sum + Math.max(0, Unread[Chat]); }
            return Sum;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppLiveListRules.Total");
            return 0;
        }
    };

    /** "(3) WACommander" - or the plain title at 0. */
    public static TitleWith = (Base: string, Total: number): string => {
        if (getCoreUtils().KErrorMode === true) { return nz(Base); }
        try {
            if (Total <= 0) { return nz(Base); }
            return "(" + Total.toLocaleString() + ") " + nz(Base);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppLiveListRules.TitleWith");
            return nz(Base);
        }
    };

    /** A newer `built` than the index in hand: the rebuild has run - re-read `index.json`. */
    public static Rebuilt = (Built: string, Have: string): boolean => {
        if (getCoreUtils().KErrorMode === true) { return false; }
        try {
            const New: number = Date.parse(nz(Built));
            if (isNaN(New) === true) { return false; }
            const Old: number = Date.parse(nz(Have));
            return isNaN(Old) === true || New > Old;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppLiveListRules.Rebuilt");
            return false;
        }
    };
}
